import { ColDef, SizeColumnsToFitGridStrategy, ValueFormatterParams } from 'ag-grid-community';
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'; // React Data Grid Component
import { format as formatDate } from 'date-fns';
import { User } from "firebase/auth";
import { Timestamp, collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useEffect, useState } from "react";
import Spinner from 'react-bootstrap/Spinner';
import { Navigate } from "react-router-dom";
import { auth, firestore } from './firebase';

type ListingInfo = {
  url: string,
  broker: string,
  broker_listing_id: string,
  title: string,
  description: string,
  asking_price?: number,
  revenue?: number,
  cash_flow?: number,
  ebitda?: number,
  cash_flow_or_ebitda?: number,
  year_established?: number,
  employee_count?: number,
  additional_attributes?: { key: string, value: unknown; }[],
};

type DBListing = ListingInfo & { createdAt: Timestamp, updatedAt: Timestamp, id: string; };

let us_dollar_format = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const dateFormatter = (params: ValueFormatterParams) => {
  return formatDate(params.value.toDate(), 'yyyy-MM-dd');
};

const priceFormatter = (params: ValueFormatterParams) => {
  if (typeof params.value === 'number' && !isNaN(params.value)) {
    return us_dollar_format.format(params.value);
  } else {
    return '';
  }
};

function titleRenderer (params: CustomCellRendererProps) {
  const { url, title } = params.data;
  return (
    <a href={url} target="_blank">{title}</a>
  );
}

const dateComparator = (filterDate: Date, cellValue: string) => {
  const filterString = formatDate(filterDate, 'yyyy-MM-dd');

  return cellValue.localeCompare(filterString);
};

const Listings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [rowData, setRowData] = useState<DBListing[]>([]);
  const colDefs: ColDef[] = [
    {
      colId: 'createdAt', headerName: 'Added', field: 'createdAt', filter: 'agDateColumnFilter', valueFormatter: dateFormatter, filterParams: {
        filterOptions: ['equals', 'greaterThanOrEqual', 'lessThanOrEqual'],
        comparator: dateComparator
      }
    },
    { colId: 'broker', headerName: 'Broker', field: 'broker', filter: 'agTextColumnFilter', },
    { headerName: 'Title', field: 'title', filter: 'agTextColumnFilter', cellRenderer: titleRenderer, },
    {
      colId: 'asking_price', headerName: 'Asking', field: 'asking_price', filter: 'agNumberColumnFilter', valueFormatter: priceFormatter, filterParams: {
        filterOptions: ['greaterThanOrEqual', 'lessThanOrEqual'],
      }
    },
    {
      colId: 'revenue', headerName: 'Revenue', field: 'revenue', filter: 'agNumberColumnFilter', valueFormatter: priceFormatter, filterParams: {
        filterOptions: ['greaterThanOrEqual', 'lessThanOrEqual'],
      }
    },
    {
      colId: 'cash_flow_or_ebitda', headerName: 'CF/EBITDA', field: 'cash_flow_or_ebitda', filter: 'agNumberColumnFilter', valueFormatter: priceFormatter, filterParams: {
        filterOptions: ['greaterThanOrEqual', 'lessThanOrEqual'],
      }
    },
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setIsLoading(false);
    });

    return unsubscribe;
  });

  function sortListings (a: DBListing, b: DBListing) {
    const a_date = formatDate(a.createdAt.toDate(), 'yyyy-MM-dd');
    const b_date = formatDate(b.createdAt.toDate(), 'yyyy-MM-dd');

    // coona_date.localeCompare(b_date);
    if (a_date > b_date) {
      return -1;
    }
    if (b_date > a_date) {
      return 1;
    }

    return a.id.localeCompare(b.id);
  }

  useEffect(() => {
    async function getListings () {
      const listingsRef = collection(firestore, 'listings');
      const q = query(listingsRef, orderBy('createdAt', 'desc'), limit(250));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((x) => {
        const listing = x.data() as DBListing;
        return {
          ...listing,
          cash_flow_or_ebitda: listing.cash_flow || listing.ebitda,
          id: x.id
        };
      });

      data.sort(sortListings);

      setRowData(data);
    }

    if (currentUser) {
      getListings();
    }
  }, [currentUser]);


  if (isLoading) {
    return <Spinner animation="border" />;
  }

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  const autoSizeStrategy: SizeColumnsToFitGridStrategy = {
    type: 'fitGridWidth',
    columnLimits: [
      { colId: 'createdAt', minWidth: 120, maxWidth: 120, },
      { colId: 'broker', minWidth: 225, maxWidth: 225 },
      { colId: 'asking_price', minWidth: 115, maxWidth: 115 },
      { colId: 'revenue', minWidth: 115, maxWidth: 115 },
      { colId: 'cash_flow_or_ebitda', minWidth: 115, maxWidth: 115 },
    ]
  };

  return (
    <div className="ag-theme-quartz">
      <AgGridReact
        domLayout='autoHeight'
        autoSizeStrategy={autoSizeStrategy}
        rowData={rowData}
        columnDefs={colDefs}
        pagination={true}
      />
    </div>
  );
};

export default Listings;